import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { PermissionQueryParams } from '../models/permissions.model';
import { BulkAssignPermissionsDto } from '../models/role.model';
import { getMenuByUserService } from '../services/menu.service';
import { permissionService } from '../services/permission.service';
import { rolePermissionService } from '../services/role-permission.service';
import { useAuthStore } from '../store/auth.store';
import { useMenuStore } from '../store/menu.store';
import { useBranchOfficeId } from './use-branch-office-id';

/**
 * Re-fetch the current user's sidebar menu and write it back into the
 * Zustand menu store. Called after any role-permission mutation so the
 * UI reflects the change without forcing a logout/login cycle.
 *
 * Only runs when the role being mutated matches the logged-in user's
 * own roleId — mutations against other roles don't affect the current
 * session's visible menu.
 */
export async function refreshCurrentUserMenu(roleId: string): Promise<void> {
  const currentUser = useAuthStore.getState().user;
  if (!currentUser?.roleId || currentUser.roleId !== roleId) return;
  try {
    const menu = await getMenuByUserService();
    useMenuStore.getState().setMenu(menu);
  } catch {
    // Best-effort — never break the mutation chain because the menu
    // refresh failed. The cashier will pick up the change on next
    // login if this errors.
  }
}

/**
 * Unconditional menu refresh for the logged-in user. Used when a
 * mutation touches the permission catalog itself (create / update /
 * delete a permission row) or deletes a role — any of those can flip
 * the current user's effective menu regardless of which role they
 * belong to.
 */
export async function refreshCurrentUserMenuAlways(): Promise<void> {
  const currentUser = useAuthStore.getState().user;
  if (!currentUser?.roleId) return;
  try {
    const menu = await getMenuByUserService();
    useMenuStore.getState().setMenu(menu);
  } catch {
    // Best-effort — see refreshCurrentUserMenu above.
  }
}

/**
 * Query keys for permissions
 */
export const permissionKeys = {
  all: ['permissions'] as const,
  lists: () => [...permissionKeys.all, 'list'] as const,
  list: (params?: PermissionQueryParams) =>
    [...permissionKeys.lists(), params] as const,
  grouped: (params?: PermissionQueryParams) =>
    [...permissionKeys.all, 'grouped', params] as const,
  details: () => [...permissionKeys.all, 'detail'] as const,
  detail: (id: string) => [...permissionKeys.details(), id] as const,
  categories: () => [...permissionKeys.all, 'categories'] as const,
  modules: () => [...permissionKeys.all, 'modules'] as const,
};

/**
 * Query keys for role permissions
 */
export const rolePermissionKeys = {
  all: ['rolePermissions'] as const,
  role: (roleId: string) => [...rolePermissionKeys.all, roleId] as const,
  roleGrouped: (roleId: string) =>
    [...rolePermissionKeys.all, roleId, 'grouped'] as const,
};

/**
 * Hook to fetch all permissions
 */
export function usePermissions(params?: PermissionQueryParams) {
  return useQuery({
    queryKey: permissionKeys.list(params),
    queryFn: () => permissionService.getAll(params),
    select: (data) => (Array.isArray(data) ? data : []),
  });
}

/**
 * Hook to fetch grouped permissions
 */
export function useGroupedPermissions(params?: PermissionQueryParams) {
  return useQuery({
    queryKey: permissionKeys.grouped(params),
    queryFn: () => permissionService.getAllGrouped(params),
    select: (data) => (Array.isArray(data) ? data : []),
  });
}

/**
 * Hook to fetch a single permission
 */
export function usePermission(id: string) {
  return useQuery({
    queryKey: permissionKeys.detail(id),
    queryFn: () => permissionService.getById(id),
    enabled: !!id,
  });
}

/**
 * Hook to fetch permission categories
 */
export function usePermissionCategories() {
  return useQuery({
    queryKey: permissionKeys.categories(),
    queryFn: () => permissionService.getCategories(),
    select: (data) => (Array.isArray(data) ? data : []),
  });
}

/**
 * Hook to fetch permission modules
 */
export function usePermissionModules() {
  return useQuery({
    queryKey: permissionKeys.modules(),
    queryFn: () => permissionService.getModules(),
    select: (data) => (Array.isArray(data) ? data : []),
  });
}

/**
 * Hook to fetch role permissions
 */
export function useRolePermissions(roleId: string) {
  // Branch in the key so switching the active branch refetches — a role's
  // grants are per-branch. The active branch is auto-injected as the
  // X-Branch-Office-Id header by the API service, so the backend scopes the
  // read; the key just keeps the cache per-branch.
  const { branchOfficeId } = useBranchOfficeId();
  return useQuery({
    queryKey: [...rolePermissionKeys.role(roleId), { branchOfficeId }],
    queryFn: () => rolePermissionService.getRolePermissions(roleId),
    enabled: !!roleId,
  });
}

/**
 * Hook to fetch role permissions grouped by category
 */
export function useRolePermissionsByCategory(roleId: string) {
  const { branchOfficeId } = useBranchOfficeId();
  return useQuery({
    queryKey: [...rolePermissionKeys.roleGrouped(roleId), { branchOfficeId }],
    queryFn: () => rolePermissionService.getRolePermissionsByCategory(roleId),
    select: (data) => (Array.isArray(data) ? data : []),
    enabled: !!roleId,
  });
}

/**
 * Hook to assign a permission to a role
 */
export function useAssignPermission(roleId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (permissionId: string) =>
      rolePermissionService.assignPermission(roleId, { permissionId }),
    onSuccess: async () => {
      // Invalidate role permissions queries
      queryClient.invalidateQueries({
        queryKey: rolePermissionKeys.role(roleId),
      });
      queryClient.invalidateQueries({
        queryKey: rolePermissionKeys.roleGrouped(roleId),
      });
      await refreshCurrentUserMenu(roleId);
    },
  });
}

/**
 * Hook to bulk assign permissions to a role
 */
export function useBulkAssignPermissions(roleId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BulkAssignPermissionsDto) =>
      rolePermissionService.bulkAssignPermissions(roleId, data),
    onSuccess: async () => {
      // Invalidate role permissions queries
      queryClient.invalidateQueries({
        queryKey: rolePermissionKeys.role(roleId),
      });
      queryClient.invalidateQueries({
        queryKey: rolePermissionKeys.roleGrouped(roleId),
      });
      await refreshCurrentUserMenu(roleId);
    },
  });
}

/**
 * Hook to remove a permission from a role
 */
export function useRemovePermission(roleId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (permissionId: string) =>
      rolePermissionService.removePermission(roleId, permissionId),
    onSuccess: async () => {
      // Invalidate role permissions queries
      queryClient.invalidateQueries({
        queryKey: rolePermissionKeys.role(roleId),
      });
      queryClient.invalidateQueries({
        queryKey: rolePermissionKeys.roleGrouped(roleId),
      });
      await refreshCurrentUserMenu(roleId);
    },
  });
}

/**
 * Hook to fetch the CURRENT user's OWN permissions via the self-scoped
 * `/roles/me/permissions` endpoint (NOT the admin-gated
 * `/roles/:roleId/permissions`). Keyed by businessId so an impersonation
 * swap refetches. First queryKey segment contains `permission` so the
 * permissions-epoch listener invalidates it alongside menu + role keys.
 */
export function useMyPermissions() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const businessId = useAuthStore((s) => s.user?.businessId);
  // Branch in the key — role permissions are per-branch, so switching the
  // active branch MUST refetch the effective set (the backend scopes
  // /roles/me/permissions by the X-Branch-Office-Id the API service injects).
  // Without this the page gates kept the previous branch's permissions until
  // a hard refresh.
  const { branchOfficeId } = useBranchOfficeId();

  return useQuery({
    queryKey: ['my-permissions', businessId ?? '', branchOfficeId ?? 'all'],
    queryFn: () => rolePermissionService.getMyPermissions(),
    enabled: isAuthenticated && !!businessId,
  });
}

/**
 * Hook to get the current user's permissions
 * Returns an array of permission codes that the user has
 */
export function useUserPermissions() {
  // Self-scoped source: `/roles/me/permissions`. Previously this read
  // the ADMIN endpoint `useRolePermissions(user.roleId)`
  // (`/roles/:roleId/permissions`), which is gated by
  // `configuration.roles_permissions.view_roles`. Any role WITHOUT that
  // admin perm (cashier / vendor / most non-admin roles) got a 403 →
  // empty permission list → `usePermissionCheck` false for EVERYTHING →
  // "Acceso restringido" on every perm-gated page even though the role
  // genuinely held the perm. The sidebar still rendered (served by the
  // self-scoped `/menu/user`), masking the divergence. `useMyPermissions`
  // reads the user's own set with no admin gate.
  const { data: rolePermissionsData, isLoading } = useMyPermissions();

  const permissions = useMemo(() => {
    if (!rolePermissionsData?.permissions) return [];
    return rolePermissionsData.permissions.map((p) => p.code);
  }, [rolePermissionsData]);

  return {
    permissions,
    isLoading,
    hasPermission: (permissionCode: string) =>
      permissions.includes(permissionCode),
    hasAnyPermission: (permissionCodes: string[]) =>
      permissionCodes.some((code) => permissions.includes(code)),
    hasAllPermissions: (permissionCodes: string[]) =>
      permissionCodes.every((code) => permissions.includes(code)),
  };
}

/**
 * Hook to check if the current user has a specific permission
 * @param permissionCode - The permission code to check (e.g., 'invoices.create')
 * @returns boolean indicating if user has the permission
 */
export function useHasPermission(permissionCode: string): boolean {
  const { permissions } = useUserPermissions();
  return permissions.includes(permissionCode);
}

/**
 * Hook to check permission with loading state
 * Use this when you need to show loading skeleton while permissions are being fetched
 * @param permissionCode - The permission code to check (e.g., 'invoices.create')
 * @returns Object with hasPermission boolean and isLoading state
 */
export function usePermissionCheck(permissionCode: string): {
  hasPermission: boolean;
  isLoading: boolean;
} {
  const { permissions, isLoading } = useUserPermissions();
  return {
    hasPermission: permissions.includes(permissionCode),
    isLoading,
  };
}

/**
 * Hook to check if the current user has any of the specified permissions
 * @param permissionCodes - Array of permission codes to check
 * @returns boolean indicating if user has at least one of the permissions
 */
export function useHasAnyPermission(permissionCodes: string[]): boolean {
  const { permissions } = useUserPermissions();
  return permissionCodes.some((code) => permissions.includes(code));
}

/**
 * Hook to check if the current user has all of the specified permissions
 * @param permissionCodes - Array of permission codes to check
 * @returns boolean indicating if user has all of the permissions
 */
export function useHasAllPermissions(permissionCodes: string[]): boolean {
  const { permissions } = useUserPermissions();
  return permissionCodes.every((code) => permissions.includes(code));
}
